import React, { useState } from 'react';
import { Icon, Button, Popup } from 'semantic-ui-react';
import { DateTime } from 'luxon';
import StatusUp from 'images/dashboard/status-up.svg';
import messages from './messages';
import i18n from 'helpers/i18n';
import settings from 'helpers/settings';
import RouteHelpers from 'helpers/routes';
import {
  ClassConnectEnrollment,
  UserEnrollment
} from 'types/api/UserEnrollment';
import { Box } from 'components/Box';
import { Recommendation } from 'types/api/Recommendation';
import { useMediaQuery } from 'react-responsive';
import { getVisibleEnrollments, mapResources } from './utils';

import {
  Wrapper,
  LearningTitle,
  ProDevelopment,
  ProIcon,
  Divider,
  LearningResources,
  ShowList,
  Resources,
  Thumbnail,
  ResourcesList,
  ResourcesCard,
  CardHeader,
  CardRow,
  BoldText,
  SmallTitle,
  LinkName,
  RowRecommendedBy,
  Date,
  ProContainer,
  BtWrapper
} from './Styled';

interface ProfessionalDevProps {
  enrollments: UserEnrollment[];
  learningResources: boolean;
  classConnectEnrollments: ClassConnectEnrollment[];
  recommendations: Recommendation[];
}

const MyTeachstone = settings.lookup('REACT_APP_MYTS_BASE_URL');

function ProfessionalDevelopment({
  enrollments,
  learningResources,
  classConnectEnrollments,
  recommendations
}: ProfessionalDevProps) {
  const visibleEnrollments = getVisibleEnrollments(enrollments);
  const showLearningHub =
    visibleEnrollments.length || classConnectEnrollments.length;
  const [isEnrollmentRowOpen, setIsEnrollmentRowOpen] = useState(false);
  const isLessThan640px = useMediaQuery({ maxWidth: 640 });

  if (
    !visibleEnrollments.length &&
    !learningResources &&
    !classConnectEnrollments.length
  ) {
    return null;
  }

  const resources = mapResources(recommendations);

  return (
    <Wrapper>
      {showLearningHub ? (
        <>
          <ProContainer>
            <ProDevelopment>
              <ProIcon src={StatusUp} alt={i18n.ft(messages.title)} />
              {i18n.ft(messages.title)}
            </ProDevelopment>
            <BtWrapper>
              <Button
                color="blue"
                as="a"
                href={RouteHelpers.getPath('td-history')}
              >
                {i18n.ft(messages.getCertificates)}
              </Button>
            </BtWrapper>
          </ProContainer>

          {visibleEnrollments.length > 0 && (
            <div className="grid gap-x-4 gap-y-2 grid-cols-[1fr_auto_auto] items-center mt-6">
              <LearningTitle
                href={RouteHelpers.getPath('canvas')}
                target="_blank"
                rel="noopener noreferrer"
              >
                {i18n.ft(messages.learning)}
                <Box as="span" ml="16px">
                  <Icon fitted name="angle right" />
                </Box>
              </LearningTitle>
              <div className="p-1 font-bold">{i18n.ft(messages.status)}</div>
              <div className="p-1 font-bold">{i18n.ft(messages.endDate)}</div>

              {visibleEnrollments.map((enrollment, index) => {
                const maxIndex = isEnrollmentRowOpen
                  ? visibleEnrollments.length
                  : 3;
                return index < maxIndex ? (
                  <React.Fragment key={`enrollments-row-${index}`}>
                    <div className="truncate overflow-hidden whitespace-nowrap">
                      {enrollment.status === 'available' ? (
                        <a
                          href={RouteHelpers.getPath('canvas-course', {
                            id: enrollment.sis_course.lms_course_id
                          })}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {enrollment.sis_course.name}
                        </a>
                      ) : (
                        <>{enrollment.sis_course.name}</>
                      )}
                    </div>
                    <div>
                      {enrollment.status && !enrollment.certificate_id ? (
                        i18n.ft(messages[enrollment.status])
                      ) : (
                        <a
                          download
                          href={`/api/v2/training_certificates/${enrollment.certificate_id}.pdf`}
                        >
                          {i18n.ft(messages.getCertificate)}
                          <Box as="span" ml="4px">
                            <Icon fitted name="angle right" />
                          </Box>{' '}
                        </a>
                      )}
                    </div>
                    <div>
                      {enrollment.end_date ? (
                        DateTime.fromISO(enrollment.end_date).toFormat(
                          'yyyy-MM-dd'
                        )
                      ) : (
                        <div className="text-center">--</div>
                      )}
                    </div>
                  </React.Fragment>
                ) : null;
              })}
            </div>
          )}
          {visibleEnrollments.length > 3 ? (
            <ShowList
              onClick={() => {
                setIsEnrollmentRowOpen(!isEnrollmentRowOpen);
              }}
            >
              {isEnrollmentRowOpen
                ? i18n.ft(messages.showLess)
                : i18n.ft(messages.showAll)}
              <Icon fitted name="arrow alternate circle right" color="blue" />
            </ShowList>
          ) : null}

          {classConnectEnrollments && classConnectEnrollments.length > 0 && (
            <div className="grid gap-x-4 gap-y-2 grid-cols-[1fr_auto_auto] items-center mt-6">
              <div
                className="bg-[#f4f8fa] w-fit px-[10px] py-[4px] rounded-[16px] font-bold text-inherit relative left-[-12px] h-fit"
                data-spotlight-popover="class-connect-section"
              >
                {i18n.ft(messages.classConnect)}
              </div>
              <div className="p-1 font-bold">{i18n.ft(messages.status)}</div>
              <div className="p-1 font-bold">{i18n.ft(messages.endDate)}</div>

              {classConnectEnrollments.map(ccEnrollment => {
                return (
                  <>
                    <div className="truncate overflow-hidden whitespace-nowrap">
                      {ccEnrollment.status === 'available' ? (
                        <a
                          href={RouteHelpers.getPath('canvas-course', {
                            id: ccEnrollment.lms_course_id
                          })}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {ccEnrollment.name}
                        </a>
                      ) : (
                        <>{ccEnrollment.name}</>
                      )}
                    </div>
                    <div>
                      {ccEnrollment.status ? (
                        i18n.ft(messages[ccEnrollment.status])
                      ) : (
                        <div className="text-center">--</div>
                      )}
                    </div>
                    <div>
                      {ccEnrollment.end_date ? (
                        DateTime.fromISO(ccEnrollment.end_date).toFormat(
                          'yyyy-MM-dd'
                        )
                      ) : (
                        <div className="text-center">--</div>
                      )}
                    </div>
                  </>
                );
              })}
            </div>
          )}
        </>
      ) : null}

      {learningResources ? (
        <>
          {showLearningHub ? (
            <Divider />
          ) : (
            <ProContainer>
              <ProDevelopment>
                <ProIcon src={StatusUp} alt={i18n.ft(messages.title)} />
                {i18n.ft(messages.title)}
              </ProDevelopment>
              <BtWrapper>
                <Button
                  fluid
                  color="blue"
                  as="a"
                  href={RouteHelpers.getPath('td-history')}
                >
                  {i18n.ft(messages.getCertificates)}
                </Button>
              </BtWrapper>
            </ProContainer>
          )}
          {recommendations?.length ? (
            isLessThan640px ? (
              <ResourcesList>
                <LearningTitle href={RouteHelpers.getPath('myts-resources')}>
                  {i18n.ft(messages.learningResources)}
                  <Box as="span" ml="16px">
                    <Icon fitted name="angle right" />
                  </Box>
                </LearningTitle>
                {resources.map((resource, index) => (
                  <ResourcesCard key={index}>
                    <CardHeader>
                      <Thumbnail src={resource.thumbnail} alt="thumbnail" />
                      <LinkName
                        href={`${MyTeachstone}/api/v1/accounts/${resource.account_id}/choose?redirect_path=resources/${resource.id}`}
                      >
                        {resource.title}
                      </LinkName>
                    </CardHeader>
                    <SmallTitle>{i18n.ft(messages.recommended)}</SmallTitle>
                    <CardRow>
                      <BoldText>{resource.user}</BoldText>
                      <Date>
                        {DateTime.fromISO(resource.date).toFormat('yyyy-MM-dd')}
                      </Date>
                    </CardRow>
                  </ResourcesCard>
                ))}
              </ResourcesList>
            ) : (
              <Resources>
                <LearningTitle href={RouteHelpers.getPath('myts-resources')}>
                  {i18n.ft(messages.learningResources)}
                  <Box as="span" ml="8px">
                    <Icon fitted name="angle right" />
                  </Box>
                </LearningTitle>
                <BoldText>{i18n.ft(messages.recommended)}</BoldText>
                <BoldText>{i18n.ft(messages.on)}</BoldText>
                {resources.map((resource, index) => (
                  <React.Fragment key={index}>
                    <CardHeader>
                      <Thumbnail src={resource.thumbnail} alt="thumbnail" />
                      <Popup
                        content={resource.title}
                        size="small"
                        trigger={
                          <LinkName
                            href={`${MyTeachstone}/api/v1/accounts/${resource.account_id}/choose?redirect_path=resources/${resource.id}`}
                          >
                            {resource.title}
                          </LinkName>
                        }
                      />
                    </CardHeader>
                    <CardHeader>
                      <RowRecommendedBy>{resource.user}</RowRecommendedBy>
                    </CardHeader>

                    <Date>
                      {DateTime.fromISO(resource.date).toFormat('yyyy-MM-dd')}
                    </Date>
                  </React.Fragment>
                ))}
              </Resources>
            )
          ) : (
            <LearningResources>
              <LearningTitle href={RouteHelpers.getPath('myts-resources')}>
                {i18n.ft(messages.learningResources)}
                <Box as="span" ml="16px">
                  <Icon fitted name="angle right" />
                </Box>
              </LearningTitle>
              <BtWrapper>
                <Button
                  fluid
                  color="blue"
                  as="a"
                  href={RouteHelpers.getPath('myts-resources')}
                >
                  {i18n.ft(messages.explore)}
                </Button>
              </BtWrapper>
            </LearningResources>
          )}
        </>
      ) : null}
    </Wrapper>
  );
}

export default ProfessionalDevelopment;
